import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'motion/react';
import { Flex, IconButton, Icon, Tooltip, iconWeight, Portal, message } from '@nula/core-ui';
import type { UIMessage } from 'ai';
import { AssistantMessageList } from './assistant-message-list';
import { AssistantInput } from './assistant-input';
import { useAssistantContext } from '../hooks/use-assistant-context';
import { useAssistantChat } from '../hooks/use-assistant-chat';
import { useCancelRun } from '../hooks/use-cancel-run';
import { useUIStore } from '@/stores/use-ui-store';
import { useSubmitFeedback } from '../hooks/use-submit-feedback';
import { FeedbackModal } from './messages/feedback-modal';
import type { FeedbackVote } from './messages/message-toolbar';
import styles from './assistant-overlay.module.css';

interface AssistantOverlayProps {
  isOpen: boolean;
  activeEntityId: string | null;
  onClose: () => void;
  onOpenFullTab: () => void;
  className?: string;
}

/**
 * Find the approvalId of the first approval-requested tool part across all messages.
 *
 * Duplicated verbatim in `AssistantFullTab.tsx` and `AssistantSidebar.tsx` per the
 * M12b plan's small-diff rule. Extract to a shared hook in a future cleanup.
 */
function findPendingApprovalId(messages: UIMessage[]): string | null {
  for (const msg of messages) {
    if (msg.role !== 'assistant') continue;
    for (const part of msg.parts ?? []) {
      if (
        (part.type.startsWith('tool-') || part.type === 'dynamic-tool') &&
        (part as { state?: string }).state === 'approval-requested'
      ) {
        const approvalId = (part as { approval?: { id?: string } }).approval?.id;
        if (approvalId) return approvalId;
      }
    }
  }
  return null;
}

export function AssistantOverlay({
  isOpen,
  activeEntityId,
  onClose,
  onOpenFullTab,
  className,
}: AssistantOverlayProps) {
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const assistantContext = useAssistantContext(activeEntityId);

  const chat = useAssistantChat();
  const { cancel } = useCancelRun();
  const { submit: submitFeedback } = useSubmitFeedback();
  const [feedbackState, setFeedbackState] = useState<{
    open: boolean;
    vote: FeedbackVote;
    runId: string | undefined;
  }>({ open: false, vote: 'up', runId: undefined });

  const handleVote = useCallback(
    (vote: FeedbackVote, runId: string | undefined) => {
      setFeedbackState({ open: true, vote, runId });
    },
    [],
  );

  const handleFeedbackSubmit = useCallback(
    async (comment: string) => {
      const { submitted } = await submitFeedback({
        runId: feedbackState.runId ?? '',
        vote: feedbackState.vote,
        comment,
      });
      if (submitted) {
        message.success('Thanks — feedback sent');
      } else {
        message.error(
          'Could not send feedback. Please try again in a moment.',
        );
      }
    },
    [feedbackState.runId, feedbackState.vote, submitFeedback],
  );

  const isRunActive = chat.status === 'streaming' || chat.status === 'submitted';
  const pendingApprovalId = useMemo(() => findPendingApprovalId(chat.messages), [chat.messages]);

  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 150);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!overlayRef.current || overlayRef.current.contains(target as Node)) return;
      // Panels the overlay opens (conversation history) portal to
      // document.body, so they sit outside the ref and counted as an outside
      // click — pressing a card's "..." dismissed the overlay before the menu
      // could open.
      if (target?.closest?.('[data-assistant-surface]')) return;
      // The menu itself portals too, and core-ui's Dropdown exposes no way to
      // mark it, so match the role instead. Without this, Pin/Delete closed
      // the overlay out from under the click.
      if (target?.closest?.('[role="menu"]')) return;
      onClose();
    };
    document.addEventListener('mousedown', handler, true);
    return () => document.removeEventListener('mousedown', handler, true);
  }, [isOpen, onClose]);

  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text || isRunActive) return;
    setInput('');
    chat.sendMessage({ text });
  }, [input, isRunActive, chat]);

  const handleStop = useCallback(() => {
    if (chat.runId) {
      try { cancel(chat.runId); } catch { /* not connected */ }
    }
    void chat.stop();
  }, [chat, cancel]);

  const handleApprove = useCallback((approvalId: string) => {
    chat.addToolApprovalResponse({ id: approvalId, approved: true });
  }, [chat]);

  const handleCancel = useCallback((approvalId: string) => {
    chat.addToolApprovalResponse({ id: approvalId, approved: false });
  }, [chat]);

  return (
    <Portal>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className={clsx(styles.root, className)}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ type: 'spring', damping: 28, stiffness: 260 }}
          >
          <Flex vertical ref={overlayRef} className={styles.panel}>
            <Flex align="center" className={styles.header}>
              <motion.div
                className={styles.headerOrb}
                animate={{ scale: [1, 1.2, 1], opacity: [0.6, 1, 0.6] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              />
              <span className={styles.headerTitle}>Nula AI</span>
              <Flex align="center" gap="xs" className={styles.headerActions}>
                <Tooltip title="Conversation history" placement="bottom">
                  <IconButton
                    size="sm"
                    aria-label="Conversation history"
                    onClick={() => useUIStore.getState().setAssistantHistoryOpen(true)}
                  >
                    <Icon name="history" size={15} weight={iconWeight.light} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Open full tab" placement="bottom">
                  <IconButton
                    size="sm"
                    aria-label="Open full tab"
                    onClick={onOpenFullTab}
                  >
                    <Icon name="open_in_full" size={15} weight={iconWeight.light} />
                  </IconButton>
                </Tooltip>
              </Flex>
            </Flex>

            <AssistantMessageList
              messages={chat.messages}
              onApprove={handleApprove}
              onCancel={handleCancel}
              pendingApprovalId={pendingApprovalId}
              onSuggestionClick={(text) => {
                setInput(text);
                inputRef.current?.focus();
              }}
              onVote={handleVote}
            />

            <AssistantInput
              ref={inputRef}
              className={styles.inputArea}
              value={input}
              onChange={setInput}
              onSend={handleSend}
              onStop={handleStop}
              isRunActive={isRunActive}
              assistantContext={assistantContext}
            />
          </Flex>
          </motion.div>
        )}
      </AnimatePresence>
      <FeedbackModal
        open={feedbackState.open}
        vote={feedbackState.vote}
        onClose={() => setFeedbackState((s) => ({ ...s, open: false }))}
        onSubmit={handleFeedbackSubmit}
      />
    </Portal>
  );
}
